import { environment } from 'src/environments/environment';
import type { ServiceCenterFilter } from './models/service-center-filter';

// 'intent' = a deliberate filter change within a loaded scope -> commit silently.
// 'background' = first validate after a (re)entered scope, or the poll/wake ->
// diff the fresh result against the shown view and banner the delta.
export type ValidateMode = 'intent' | 'background';

// Every shop selection — concrete ids, OTR, and the IN_MY_SHOPS / IN_ANY_SHOP
// macros — is a client-side subset of the broad permitted universe, so the
// universe fetch carries no shop filter and a shop change never refetches.
export function stripShopFilter(filter: ServiceCenterFilter): ServiceCenterFilter {
  return { ...filter, shopLocations: [] };
}

const scDebugEnabled = (): boolean =>
  typeof localStorage !== 'undefined' && localStorage.getItem('sc.debug') === '1';

// TEST HOOK: comma list of TenantFeatures names treated as enabled, ORed with
// the tenant's real features. Set via ?scFlags= on the Service Center route. Unset ⇒ no effect.
export const SC_FLAGS_STORAGE_KEY = 'sc.flags';

// A feature a user can grant themselves isn't a feature gate. Allow-list, not deny-list:
// a new environment must opt in rather than silently inherit the hook.
const OVERRIDABLE_ENVS: readonly string[] = ['local', 'dev', 'test'];
export const scFlagOverridesAllowed = (): boolean => OVERRIDABLE_ENVS.includes(environment.env);

export const scFlagOverrides = (): ReadonlySet<string> =>
  new Set((scFlagOverridesAllowed() && typeof localStorage !== 'undefined' ? localStorage.getItem(SC_FLAGS_STORAGE_KEY) ?? '' : '')
    .split(',').map(s => s.trim()).filter(Boolean));

// `data` is a thunk so call-site payloads aren't built on hot paths when debug is off.
export function scLog(tag: string, data: () => unknown): void {
  if (scDebugEnabled()) console.log(tag, JSON.stringify(data(), null, 2));
}

export function idsMatchInOrder(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// Scope = the permitted universe a cache must drop prior records on when it
// changes: group (+ weightRatings for assets, server-only and not in
// filterAssets). No shop dimension — every shop selection is client-filtered
// over the broad universe. Home is scope for the location cache but a
// client-filterable row subset for assets (matched on asset.locationGroups), so
// the asset cache passes includeHome=false and folds home into filterAssets.
export function computeScopeKey(
  contextKey: string,
  filter: ServiceCenterFilter,
  includeWeightRatings = false,
  includeHome = true,
): string {
  const home = includeHome ? `|home:${[...(filter.homeLocationGroupIds ?? [])].sort().join(',')}` : '';
  const base = `${contextKey}${home}|group:${filter.groupId ?? ''}`;
  if (!includeWeightRatings) return base;
  const weights = [...(filter.weightRatings ?? [])].sort().join(',');
  return `${base}|wr:${weights}`;
}
